import { useState, useEffect } from 'react'
import { Track, RailCar, CarState, getAvailableLocomotives, getTrackRailCars } from '@nyos/shared'

interface FootprintGridProps {
  availableTracks?: Track[]
  onTrackSelect?: (trackId: string) => void
  onLocomotiveSelect?: (locomotive: RailCar) => void
  onLocomotiveUnselect?: () => void
  facilityId?: string
  selectedLocomotiveId?: string
  selectedTrackId?: string
}

export default function FootprintGrid({ availableTracks, onTrackSelect, onLocomotiveSelect, onLocomotiveUnselect, facilityId, selectedLocomotiveId, selectedTrackId }: FootprintGridProps) {
  const [selectedTrack, setSelectedTrack] = useState<string>('')
  const [selectedLocomotive, setSelectedLocomotive] = useState<string>('')
  const [trackCars, setTrackCars] = useState<RailCar[]>([])
  const [availableLocomotives, setAvailableLocomotives] = useState<RailCar[]>([])
  const [isLoadingCars, setIsLoadingCars] = useState(false)
  const [isLoadingLocomotives, setIsLoadingLocomotives] = useState(false)

  // Sync internal state with props when they change (for remount scenarios)
  useEffect(() => {
    if (selectedTrackId !== undefined) {
      setSelectedTrack(selectedTrackId)
    }
  }, [selectedTrackId])

  useEffect(() => {
    if (selectedLocomotiveId !== undefined) {
      setSelectedLocomotive(selectedLocomotiveId)
    }
  }, [selectedLocomotiveId])

  const handleTrackSelect = (trackId: string) => {
    setSelectedTrack(trackId)
    onTrackSelect?.(trackId)
  }

  const handleLocomotiveSelect = (locomotiveId: string) => {
    setSelectedLocomotive(locomotiveId)
    // Find the full locomotive object from availableLocomotives
    const locomotive = availableLocomotives.find(loco => loco.id === locomotiveId)
    if (locomotive) {
      onLocomotiveSelect?.(locomotive)
    }
  }

  const handleLocomotiveClear = () => {
    setSelectedLocomotive('')
    onLocomotiveUnselect?.()
  }

  // Fetch available locomotives for the yard
  useEffect(() => {
    const fetchLocomotives = async () => {
      if (!facilityId) {
        setAvailableLocomotives([])
        return
      }

      setIsLoadingLocomotives(true)
      try {
        const locomotives = await getAvailableLocomotives(facilityId)
        setAvailableLocomotives(locomotives)
      } catch (error) {
        console.error('Error fetching available locomotives:', error)
        setAvailableLocomotives([])
      } finally {
        setIsLoadingLocomotives(false)
      }
    }

    fetchLocomotives()
  }, [facilityId])

  useEffect(() => {
    const fetchTrackCars = async () => {
      if (!selectedTrack) {
        setTrackCars([])
        return
      }

      setIsLoadingCars(true)
      try {
        const cars = await getTrackRailCars(selectedTrack)
        setTrackCars(cars)
      } catch (error) {
        console.error('Error fetching track cars:', error)
        setTrackCars([])
      } finally {
        setIsLoadingCars(false)
      }
    }

    fetchTrackCars()
  }, [selectedTrack])

  const getHazmatBadgeColor = (hazmatClass?: string) => {
    if (!hazmatClass) return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
  }

  const getStateBadgeColor = (state?: CarState) => {
    switch (state) {
      case 'IN_MOTION':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'STATIONARY':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'MAINTENANCE':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'INTERCHANGE_INBOUND':
      case 'INTERCHANGE_OUTBOUND':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
      case 'DEPARTED':
      case 'OFF_NETWORK':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
      case 'PRE_ADVISED':
      default:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    }
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Target + Switcher selection */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold text-foreground">Target Track:</label>
          <select
            value={selectedTrack}
            onChange={(e) => handleTrackSelect(e.target.value)}
            disabled={!availableTracks || availableTracks.length === 0}
            className="px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-sm disabled:opacity-50 flex-1"
          >
            <option value="">-- Select a track --</option>
            {availableTracks && availableTracks.map((track) => (
              <option key={track.id} value={track.id}>{track.name}</option>
            ))}
          </select>
        </div>

        {/* Yard Switcher locomotive */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold text-foreground">Yard Switcher:</label>
          <select
            value={selectedLocomotive}
            onChange={(e) => e.target.value ? handleLocomotiveSelect(e.target.value) : handleLocomotiveClear()}
            disabled={isLoadingLocomotives}
            className="px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-sm disabled:opacity-50 flex-1"
          >
            <option value="">-- Select locomotive --</option>
            {availableLocomotives && availableLocomotives.map((loco) => (
              <option key={loco.id} value={loco.id}>{loco.reportingMark} ({loco.lengthFeet?.toFixed(0)}ft)</option>
            ))}
          </select>
        </div>
      </div>

      {/* Physical Car Sequence Display */}
      {isLoadingCars ? (
        <div className="text-center text-muted-foreground py-8">
          Loading track cars...
        </div>
      ) : trackCars.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">
          {selectedTrack ? 'No cars on this track.' : 'Select a track to view physical car sequence.'}
        </div>
      ) : (
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-semibold text-foreground">Seq</th>
                <th className="text-left py-2 px-3 font-semibold text-foreground">Car ID</th>
                <th className="text-left py-2 px-3 font-semibold text-foreground">Type/Length</th>
                <th className="text-left py-2 px-3 font-semibold text-foreground">Status</th>
                <th className="text-left py-2 px-3 font-semibold text-foreground">Hazmat</th>
              </tr>
            </thead>
            <tbody>
              {trackCars.map((car, index) => (
                <tr key={car.id} className="border-b border-border hover:bg-muted/50">
                  <td className="py-2 px-3 text-muted-foreground">{index + 1}</td>
                  <td className="py-2 px-3 font-mono text-foreground">{car.reportingMark}</td>
                  <td className="py-2 px-3 text-foreground">
                    {car.assetType} - {car.lengthFeet?.toFixed(0)}ft
                  </td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStateBadgeColor(car.state)}`}>
                      {car.state}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getHazmatBadgeColor(car.hazmatClass)}`}>
                      {car.hazmatClass || 'CLEAR'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
